import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import type { ChangeEvent, FormEvent, ReactNode } from "react";
import { useCallback, useState } from "react";
import { Title, useNotify, useTranslate } from "react-admin";

import { RequirePermission } from "../components/RequirePermission";
import { SalesforceSyncRuns } from "../components/SalesforceSyncRuns";
import { SettingsSection } from "../components/SettingsSection";
import { ResourceName } from "../constants/resources";
import {
  CheckSalesforceConnectionDocument,
  type CheckSalesforceConnectionMutation,
  type CheckSalesforceConnectionMutationVariables,
  SalesforceIntegrationDocument,
  type SalesforceIntegrationFieldsFragment,
  type SalesforceIntegrationQuery,
  SalesforceSyncSchedule,
  UpdateSalesforceIntegrationDocument,
  type UpdateSalesforceIntegrationMutation,
  type UpdateSalesforceIntegrationMutationVariables,
} from "../generated/graphql";
import { formatCrmCount, formatCrmDateTime } from "../lib/crm";
import { captureException } from "../lib/errorHandling";
import {
  DAILY_TIMES,
  DEFAULT_DAILY_TIME,
  DEFAULT_INTERVAL_HOURS,
  DEFAULT_OUTSIDE_INTERVAL_HOURS,
  DEFAULT_WINDOW_END,
  DEFAULT_WINDOW_INTERVAL_MINUTES,
  DEFAULT_WINDOW_START,
  INTERVAL_HOURS,
  OUTSIDE_INTERVAL_HOURS,
  salesforceIntegrationErrorKey,
  WINDOW_INTERVAL_MINUTES,
} from "../lib/salesforceIntegration";
import { space, typography } from "../theme/designTokens";

const SELECT_MAX_WIDTH_PX = 240;

type TextFieldChange = ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

type ConnectionCheck = { severity: "success" | "error"; message: string };

type SalesforceIntegrationFormProps = {
  settings: SalesforceIntegrationFieldsFragment;
};

const SalesforceIntegrationForm = ({ settings }: SalesforceIntegrationFormProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [loginUrl, setLoginUrl] = useState(settings.loginUrl ?? "");
  const [clientId, setClientId] = useState(settings.clientId ?? "");
  const [clientSecret, setClientSecret] = useState("");
  const [anonymize, setAnonymize] = useState(settings.anonymize);
  const [schedule, setSchedule] = useState(settings.schedule);
  const [dailyTime, setDailyTime] = useState(settings.dailyTime ?? DEFAULT_DAILY_TIME);
  const [intervalHours, setIntervalHours] = useState(settings.intervalHours ?? DEFAULT_INTERVAL_HOURS);
  const [windowStart, setWindowStart] = useState(settings.windowStart ?? DEFAULT_WINDOW_START);
  const [windowEnd, setWindowEnd] = useState(settings.windowEnd ?? DEFAULT_WINDOW_END);
  const [windowIntervalMinutes, setWindowIntervalMinutes] = useState(
    settings.windowIntervalMinutes ?? DEFAULT_WINDOW_INTERVAL_MINUTES
  );
  const [outsideIntervalHours, setOutsideIntervalHours] = useState(
    settings.outsideIntervalHours ?? DEFAULT_OUTSIDE_INTERVAL_HOURS
  );
  const [error, setError] = useState<string | null>(null);
  const [update, { loading: saving }] = useMutation<
    UpdateSalesforceIntegrationMutation,
    UpdateSalesforceIntegrationMutationVariables
  >(UpdateSalesforceIntegrationDocument);
  const [check, setCheck] = useState<ConnectionCheck | null>(null);
  const [checkConnection, { loading: checking }] = useMutation<
    CheckSalesforceConnectionMutation,
    CheckSalesforceConnectionMutationVariables
  >(CheckSalesforceConnectionDocument);

  // A result only describes the connection it was checked with.
  const handleLoginUrl = useCallback((event: TextFieldChange) => {
    setLoginUrl(event.target.value);
    setCheck(null);
  }, []);
  const handleClientId = useCallback((event: TextFieldChange) => {
    setClientId(event.target.value);
    setCheck(null);
  }, []);
  const handleClientSecret = useCallback((event: TextFieldChange) => {
    setClientSecret(event.target.value);
    setCheck(null);
  }, []);
  const handleAnonymize = useCallback((event: ChangeEvent<HTMLInputElement>) => setAnonymize(event.target.checked), []);
  const handleSchedule = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setSchedule(event.target.value as SalesforceSyncSchedule),
    []
  );
  const handleDailyTime = useCallback((event: TextFieldChange) => setDailyTime(event.target.value), []);
  const handleIntervalHours = useCallback((event: TextFieldChange) => setIntervalHours(Number(event.target.value)), []);
  // The window ends after it starts: a later start moves the end along.
  const handleWindowStart = useCallback((event: TextFieldChange) => {
    const start = event.target.value;
    setWindowStart(start);
    setWindowEnd(end => (end > start ? end : DAILY_TIMES[DAILY_TIMES.indexOf(start) + 1]));
  }, []);
  const handleWindowEnd = useCallback((event: TextFieldChange) => setWindowEnd(event.target.value), []);
  const handleWindowIntervalMinutes = useCallback(
    (event: TextFieldChange) => setWindowIntervalMinutes(Number(event.target.value)),
    []
  );
  const handleOutsideIntervalHours = useCallback(
    (event: TextFieldChange) => setOutsideIntervalHours(Number(event.target.value)),
    []
  );
  const isWorkdayWindow = schedule === SalesforceSyncSchedule.WorkdayWindow;

  const hasConnection =
    loginUrl.trim() !== "" && clientId.trim() !== "" && (settings.hasClientSecret || clientSecret !== "");
  const canSave = !saving && hasConnection;
  const canCheck = !checking && hasConnection;
  // The next run belongs to the saved schedule; an edited one hides it.
  const showNextRun =
    !!settings.nextRunAt &&
    schedule === settings.schedule &&
    dailyTime === (settings.dailyTime ?? DEFAULT_DAILY_TIME) &&
    intervalHours === (settings.intervalHours ?? DEFAULT_INTERVAL_HOURS) &&
    windowStart === (settings.windowStart ?? DEFAULT_WINDOW_START) &&
    windowEnd === (settings.windowEnd ?? DEFAULT_WINDOW_END) &&
    windowIntervalMinutes === (settings.windowIntervalMinutes ?? DEFAULT_WINDOW_INTERVAL_MINUTES) &&
    outsideIntervalHours === (settings.outsideIntervalHours ?? DEFAULT_OUTSIDE_INTERVAL_HOURS);

  const handleCheck = useCallback(async () => {
    setCheck(null);
    try {
      const { data } = await checkConnection({
        variables: {
          input: { loginUrl: loginUrl.trim(), clientId: clientId.trim(), clientSecret: clientSecret || null },
        },
      });
      const result = data?.checkSalesforceConnection;
      if (!result) return;
      setCheck(
        result.ok
          ? {
              severity: "success",
              message: translate("salesforceIntegration.connection.ok", {
                remaining: formatCrmCount(result.dailyApiRequestsRemaining ?? 0),
                max: formatCrmCount(result.dailyApiRequestsMax ?? 0),
              }),
            }
          : {
              severity: "error",
              message: translate("salesforceIntegration.connection.failed", { code: result.errorCode ?? "unknown" }),
            }
      );
    } catch (checkError) {
      const key = salesforceIntegrationErrorKey(checkError);
      if (key === null) captureException(checkError, "salesforce/integration: check connection");
      setCheck({ severity: "error", message: translate(key ?? "salesforceIntegration.connection.checkError") });
    }
  }, [checkConnection, clientId, clientSecret, loginUrl, translate]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setError(null);
      try {
        await update({
          variables: {
            input: {
              loginUrl: loginUrl.trim(),
              clientId: clientId.trim(),
              clientSecret: clientSecret || null,
              anonymize,
              schedule,
              dailyTime: schedule === SalesforceSyncSchedule.Daily ? dailyTime : null,
              intervalHours: schedule === SalesforceSyncSchedule.Interval ? intervalHours : null,
              windowStart: isWorkdayWindow ? windowStart : null,
              windowEnd: isWorkdayWindow ? windowEnd : null,
              windowIntervalMinutes: isWorkdayWindow ? windowIntervalMinutes : null,
              outsideIntervalHours: isWorkdayWindow ? outsideIntervalHours : null,
            },
          },
          refetchQueries: [{ query: SalesforceIntegrationDocument }],
          awaitRefetchQueries: true,
        });
        notify(translate("salesforceIntegration.saved"), { type: "success" });
      } catch (saveError) {
        const key = salesforceIntegrationErrorKey(saveError);
        if (key === null) captureException(saveError, "salesforce/integration: save settings");
        setError(translate(key ?? "salesforceIntegration.saveError"));
      }
    },
    [
      anonymize,
      clientId,
      clientSecret,
      dailyTime,
      intervalHours,
      isWorkdayWindow,
      loginUrl,
      notify,
      outsideIntervalHours,
      schedule,
      translate,
      update,
      windowEnd,
      windowIntervalMinutes,
      windowStart,
    ]
  );

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      <Stack gap={space.sm2}>
        <SettingsSection title={translate("salesforceIntegration.connection.title")}>
          <TextField
            fullWidth
            required
            label={translate("salesforceIntegration.connection.loginUrl")}
            helperText={translate("salesforceIntegration.connection.loginUrlHint")}
            value={loginUrl}
            onChange={handleLoginUrl}
          />
          <TextField
            fullWidth
            required
            label={translate("salesforceIntegration.connection.clientId")}
            value={clientId}
            onChange={handleClientId}
          />
          <TextField
            fullWidth
            required={!settings.hasClientSecret}
            type="password"
            autoComplete="new-password"
            label={translate("salesforceIntegration.connection.clientSecret")}
            helperText={
              settings.hasClientSecret ? translate("salesforceIntegration.connection.clientSecretStored") : undefined
            }
            value={clientSecret}
            onChange={handleClientSecret}
          />
          <Box>
            <Button type="button" variant="outlined" onClick={handleCheck} disabled={!canCheck}>
              {translate("salesforceIntegration.connection.check")}
            </Button>
          </Box>
          {check && <Alert severity={check.severity}>{check.message}</Alert>}
        </SettingsSection>

        <SettingsSection title={translate("salesforceIntegration.anonymization.title")}>
          <FormControlLabel
            control={
              <Switch checked={anonymize} disabled={settings.anonymizationRequired} onChange={handleAnonymize} />
            }
            label={translate("salesforceIntegration.anonymization.label")}
          />
          {settings.anonymizationRequired ? (
            <Typography variant="body2" color="text.secondary">
              {translate("salesforceIntegration.anonymization.required")}
            </Typography>
          ) : (
            !anonymize && (
              <Alert severity="warning">{translate("salesforceIntegration.anonymization.realDataWarning")}</Alert>
            )
          )}
        </SettingsSection>

        <SettingsSection title={translate("salesforceIntegration.schedule.title")}>
          <RadioGroup
            row
            aria-label={translate("salesforceIntegration.schedule.title")}
            value={schedule}
            onChange={handleSchedule}
          >
            <FormControlLabel
              value={SalesforceSyncSchedule.Off}
              control={<Radio />}
              label={translate("salesforceIntegration.schedule.off")}
            />
            <FormControlLabel
              value={SalesforceSyncSchedule.Daily}
              control={<Radio />}
              label={translate("salesforceIntegration.schedule.daily")}
            />
            <FormControlLabel
              value={SalesforceSyncSchedule.Interval}
              control={<Radio />}
              label={translate("salesforceIntegration.schedule.interval")}
            />
            <FormControlLabel
              value={SalesforceSyncSchedule.WorkdayWindow}
              control={<Radio />}
              label={translate("salesforceIntegration.schedule.workdayWindow")}
            />
          </RadioGroup>
          {schedule === SalesforceSyncSchedule.Daily && (
            <TextField
              select
              label={translate("salesforceIntegration.schedule.dailyTime")}
              value={dailyTime}
              onChange={handleDailyTime}
              sx={{ maxWidth: SELECT_MAX_WIDTH_PX }}
            >
              {DAILY_TIMES.map(time => (
                <MenuItem key={time} value={time}>
                  {time}
                </MenuItem>
              ))}
            </TextField>
          )}
          {schedule === SalesforceSyncSchedule.Interval && (
            <TextField
              select
              label={translate("salesforceIntegration.schedule.intervalHours")}
              value={intervalHours}
              onChange={handleIntervalHours}
              sx={{ maxWidth: SELECT_MAX_WIDTH_PX }}
            >
              {INTERVAL_HOURS.map(hours => (
                <MenuItem key={hours} value={hours}>
                  {translate("salesforceIntegration.schedule.hours", { smart_count: hours })}
                </MenuItem>
              ))}
            </TextField>
          )}
          {isWorkdayWindow && (
            <>
              <TextField
                select
                label={translate("salesforceIntegration.schedule.windowStart")}
                value={windowStart}
                onChange={handleWindowStart}
                sx={{ maxWidth: SELECT_MAX_WIDTH_PX }}
              >
                {DAILY_TIMES.slice(0, -1).map(time => (
                  <MenuItem key={time} value={time}>
                    {time}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={translate("salesforceIntegration.schedule.windowEnd")}
                value={windowEnd}
                onChange={handleWindowEnd}
                sx={{ maxWidth: SELECT_MAX_WIDTH_PX }}
              >
                {DAILY_TIMES.filter(time => time > windowStart).map(time => (
                  <MenuItem key={time} value={time}>
                    {time}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={translate("salesforceIntegration.schedule.windowIntervalMinutes")}
                value={windowIntervalMinutes}
                onChange={handleWindowIntervalMinutes}
                sx={{ maxWidth: SELECT_MAX_WIDTH_PX }}
              >
                {WINDOW_INTERVAL_MINUTES.map(minutes => (
                  <MenuItem key={minutes} value={minutes}>
                    {translate("salesforceIntegration.schedule.minutes", { smart_count: minutes })}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={translate("salesforceIntegration.schedule.outsideIntervalHours")}
                value={outsideIntervalHours}
                onChange={handleOutsideIntervalHours}
                sx={{ maxWidth: SELECT_MAX_WIDTH_PX }}
              >
                {OUTSIDE_INTERVAL_HOURS.map(hours => (
                  <MenuItem key={hours} value={hours}>
                    {translate("salesforceIntegration.schedule.hours", { smart_count: hours })}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="body2" color="text.secondary">
                {translate("salesforceIntegration.schedule.workdayWindowHint")}
              </Typography>
              {anonymize && (
                <Alert severity="warning">{translate("salesforceIntegration.schedule.anonymizedWarning")}</Alert>
              )}
            </>
          )}
          {showNextRun && settings.nextRunAt && (
            <Typography variant="body2" color="text.secondary">
              {new Date(settings.nextRunAt) <= new Date()
                ? translate("salesforceIntegration.schedule.nextRunDue")
                : translate("salesforceIntegration.schedule.nextRun", { time: formatCrmDateTime(settings.nextRunAt) })}
            </Typography>
          )}
        </SettingsSection>

        {error && <Alert severity="error">{error}</Alert>}
        <Box>
          <Button type="submit" variant="contained" disabled={!canSave}>
            {translate("salesforceIntegration.save")}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

const SalesforceIntegration = () => {
  const translate = useTranslate();
  const { data, loading, error } = useQuery<SalesforceIntegrationQuery>(SalesforceIntegrationDocument, {
    fetchPolicy: "cache-and-network",
  });
  const settings = data?.salesforceIntegration;

  let content: ReactNode = null;
  if (error) content = <Alert severity="error">{translate("salesforceIntegration.loadError")}</Alert>;
  else if (loading && !settings) content = <CircularProgress aria-label={translate("salesforceIntegration.loading")} />;
  else if (settings)
    // The key resets the form, and so the typed secret, after a save refetch.
    content = <SalesforceIntegrationForm key={settings.updatedAt ?? "unsaved"} settings={settings} />;

  return (
    <Stack gap={space.sm2} data-testid="salesforce-integration-page">
      <Title title={translate("salesforceIntegration.title")} />
      {/* The form stays narrow; the runs table needs the full width for its columns. */}
      <Stack gap={space.sm2} maxWidth={760}>
        <Box>
          <Typography variant="h5" fontWeight={typography.fontWeight.semibold}>
            {translate("salesforceIntegration.heading")}
          </Typography>
          <Typography color="text.secondary">{translate("salesforceIntegration.description")}</Typography>
        </Box>
        {content}
      </Stack>
      {settings && <SalesforceSyncRuns canStart={settings.hasClientSecret} />}
    </Stack>
  );
};

export const SalesforceIntegrationPage = () => (
  <RequirePermission permission={ResourceName.SalesforceIntegration}>
    <SalesforceIntegration />
  </RequirePermission>
);
